import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Fade,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import CloseIcon from '@material-ui/icons/Close';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import PrintIcon from '@material-ui/icons/Print';
import FitnessCenterIcon from '@material-ui/icons/FitnessCenter';
import PowerSettingsNewIcon from '@material-ui/icons/PowerSettingsNew';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import StorefrontIcon from '@material-ui/icons/Storefront';

const useStyles = makeStyles((theme) => ({
  barContainer: {
    position: 'fixed',
    bottom: theme.spacing(3),
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 1250,
    backgroundColor: 'rgba(23, 73, 127, 0.95)',
    backdropFilter: 'blur(10px)',
    color: '#ffffff',
    borderRadius: 16,
    padding: '8px 16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    maxWidth: '94vw',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    transition: 'all 0.3s ease',
    [theme.breakpoints.down('xs')]: {
      bottom: theme.spacing(2),
      padding: '8px 12px',
      gap: theme.spacing(1),
      borderRadius: 12,
    },
  },
  countChip: {
    backgroundColor: '#ffffff',
    color: '#17497F',
    fontWeight: 700,
    fontSize: '0.85rem',
  },
  actionBtn: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.85rem',
    padding: '6px 12px',
    color: '#ffffff',
    borderColor: 'rgba(255, 255, 255, 0.4)',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      borderColor: '#ffffff',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.75rem',
      padding: '4px 8px',
    },
  },
  deactivateBtn: {
    borderRadius: 8,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.85rem',
    padding: '6px 12px',
    backgroundColor: '#d32f2f',
    color: '#ffffff',
    '&:hover': {
      backgroundColor: '#b71c1c',
    },
    [theme.breakpoints.down('xs')]: {
      fontSize: '0.75rem',
      padding: '4px 8px',
    },
  },
  clearBtn: {
    color: 'rgba(255, 255, 255, 0.8)',
    padding: 4,
    '&:hover': {
      color: '#ffffff',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  divider: {
    backgroundColor: 'rgba(255, 255, 255, 0.25)',
    height: 24,
  },
}));

const UIBatchActionBar = ({
  selectedCount = 0,
  totalCount = 0,
  onClearSelection,
  onSelectAll,
  onCatalogVisibility, // (show: boolean) => void
  onPrintLabels, // () => void
  onScaleExport, // () => void
  onDeactivate, // () => void (for services)
  selectedItems = [],
  loading = false,
  itemLabel = 'itens',
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));

  const [catalogAnchor, setCatalogAnchor] = useState(null);
  const [confirmDeactivateOpen, setConfirmDeactivateOpen] = useState(false);

  if (selectedCount === 0) return null;

  const toDeactivate = (selectedItems || []).filter(item => Number(item.isActive) === 1);
  const toActivate = (selectedItems || []).filter(item => Number(item.isActive) !== 1);
  const isAllToActivate = toActivate.length > 0 && toDeactivate.length === 0;
  const isMixed = toActivate.length > 0 && toDeactivate.length > 0;
  const isAllToDeactivate = !isAllToActivate && !isMixed;

  const handleOpenCatalogMenu = (event) => {
    setCatalogAnchor(event.currentTarget);
  };

  const handleCloseCatalogMenu = () => {
    setCatalogAnchor(null);
  };

  const handleCatalogAction = (show) => {
    handleCloseCatalogMenu();
    if (onCatalogVisibility) {
      onCatalogVisibility(show);
    }
  };

  return (
    <>
      <Fade in={selectedCount > 0} timeout={250}>
        <Box className={classes.barContainer}>
          {/* Esquerda: Contador e Limpar */}
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            <Chip
              size="small"
              className={classes.countChip}
              label={`${selectedCount} ${itemLabel}`}
            />
            {totalCount > selectedCount && onSelectAll && !isMobile && (
              <Button
                size="small"
                style={{ color: '#E0E0E0', textTransform: 'none', fontSize: '0.75rem', padding: '2px 6px' }}
                onClick={onSelectAll}
              >
                Selecionar todos ({totalCount})
              </Button>
            )}
            <Tooltip title="Limpar seleção">
              <IconButton
                size="small"
                className={classes.clearBtn}
                onClick={onClearSelection}
                disabled={loading}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Divider orientation="vertical" flexItem className={classes.divider} />

          {/* Direita: Ações em Lote */}
          <Box display="flex" alignItems="center" style={{ gap: 8, flexWrap: 'wrap' }}>
            {/* Ação 1: Exibição no Catálogo */}
            {onCatalogVisibility && (
              <>
                <Button
                  variant="outlined"
                  className={classes.actionBtn}
                  startIcon={<StorefrontIcon fontSize="small" />}
                  endIcon={<ArrowDropDownIcon fontSize="small" />}
                  onClick={handleOpenCatalogMenu}
                  disabled={loading}
                >
                  {isMobile ? 'Catálogo' : 'Catálogo Virtual'}
                </Button>
                <Menu
                  anchorEl={catalogAnchor}
                  keepMounted
                  open={Boolean(catalogAnchor)}
                  onClose={handleCloseCatalogMenu}
                  getContentAnchorEl={null}
                  anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                  transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                  <MenuItem onClick={() => handleCatalogAction(true)}>
                    <VisibilityIcon fontSize="small" style={{ color: '#17497F', marginRight: 8 }} />
                    Exibir no Catálogo ({selectedCount})
                  </MenuItem>
                  <MenuItem onClick={() => handleCatalogAction(false)}>
                    <VisibilityOffIcon fontSize="small" style={{ color: '#757575', marginRight: 8 }} />
                    Ocultar do Catálogo ({selectedCount})
                  </MenuItem>
                </Menu>
              </>
            )}

            {/* Ação 2: Impressão de Etiquetas */}
            {onPrintLabels && (
              <Button
                variant="outlined"
                className={classes.actionBtn}
                startIcon={<PrintIcon fontSize="small" />}
                onClick={onPrintLabels}
                disabled={loading}
              >
                {isMobile ? 'Etiquetas' : 'Imprimir Etiquetas'}
              </Button>
            )}

            {/* Ação 3: Carga de Balança (MGV) */}
            {onScaleExport && (
              <Button
                variant="outlined"
                className={classes.actionBtn}
                startIcon={<FitnessCenterIcon fontSize="small" />}
                onClick={onScaleExport}
                disabled={loading}
              >
                {isMobile ? 'Balança' : 'Carga Balança'}
              </Button>
            )}

            {/* Ação 3: Ativação / Desativação em Massa (somente serviços) */}
            {onDeactivate && (
              <Button
                variant="contained"
                className={classes.deactivateBtn}
                style={{
                  backgroundColor: isAllToActivate ? '#2e7d32' : isMixed ? '#e65100b2' : '#d32f2fe1',
                }}
                startIcon={<PowerSettingsNewIcon fontSize="small" />}
                onClick={() => setConfirmDeactivateOpen(true)}
                disabled={loading}
              >
                {isMobile
                  ? (isAllToActivate ? 'Ativar' : isMixed ? 'Alternar' : 'Desativar')
                  : (isAllToActivate ? 'Ativar selecionados' : isMixed ? 'Alternar status' : 'Desativar selecionados')}
              </Button>
            )}

            {loading && (
              <CircularProgress size={20} style={{ color: '#ffffff', marginLeft: 4 }} />
            )}
          </Box>
        </Box>
      </Fade>

      {/* Diálogo de confirmação para Alternância de Status / Desativação em massa */}
      {onDeactivate && (
        <Dialog
          open={confirmDeactivateOpen}
          onClose={() => setConfirmDeactivateOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle
            style={{
              fontWeight: 700,
              color:
                toActivate.length > 0 && toDeactivate.length === 0
                  ? '#2e7d32'
                  : toDeactivate.length > 0 && toActivate.length === 0
                    ? '#d32f2f'
                    : '#17497F',
            }}
          >
            {toActivate.length > 0 && toDeactivate.length > 0
              ? 'Alternar status dos serviços'
              : toActivate.length > 0
                ? 'Ativar selecionados'
                : 'Desativar selecionados'}
          </DialogTitle>
          <DialogContent>
            <DialogContentText style={{ marginBottom: 16 }}>
              O sistema inverte o status atual dos itens selecionados. Confira abaixo quais serão <strong>ativados</strong> e quais serão <strong>desativados</strong>:
            </DialogContentText>

            {toDeactivate.length > 0 && (
              <Box mb={2} p={1.5} style={{ backgroundColor: '#fdeded', borderRadius: 8, border: '1px solid #f5c6cb' }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Chip
                    size="small"
                    label={`${toDeactivate.length} a desativar`}
                    style={{ backgroundColor: '#d32f2f', color: '#fff', fontWeight: 700, marginRight: 8 }}
                  />
                  <Typography variant="body2" style={{ color: '#b71c1c', fontWeight: 600 }}>
                    Atualmente Ativos &rarr; ficarão <strong>Inativos</strong>
                  </Typography>
                </Box>
                <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                  {toDeactivate.map((item) => (
                    <Chip
                      key={item.id}
                      size="small"
                      variant="outlined"
                      label={`#${item.id} - ${item.name}`}
                      style={{ borderColor: '#d32f2f', color: '#b71c1c', backgroundColor: '#fff', fontSize: '0.78rem' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            {toActivate.length > 0 && (
              <Box mb={2} p={1.5} style={{ backgroundColor: '#edf7ed', borderRadius: 8, border: '1px solid #c3e6cb' }}>
                <Box display="flex" alignItems="center" mb={1}>
                  <Chip
                    size="small"
                    label={`${toActivate.length} a ativar`}
                    style={{ backgroundColor: '#2e7d32', color: '#fff', fontWeight: 700, marginRight: 8 }}
                  />
                  <Typography variant="body2" style={{ color: '#1b5e20', fontWeight: 600 }}>
                    Atualmente Inativos &rarr; ficarão <strong>Ativos</strong>
                  </Typography>
                </Box>
                <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                  {toActivate.map((item) => (
                    <Chip
                      key={item.id}
                      size="small"
                      variant="outlined"
                      label={`#${item.id} - ${item.name}`}
                      style={{ borderColor: '#2e7d32', color: '#1b5e20', backgroundColor: '#fff', fontSize: '0.78rem' }}
                    />
                  ))}
                </Box>
              </Box>
            )}

            <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: 8 }}>
              Ao confirmar, a alteração de status será aplicada a todos os {selectedCount} serviços selecionados.
            </Typography>
          </DialogContent>
          <DialogActions style={{ padding: '12px 24px' }}>
            <Button
              onClick={() => setConfirmDeactivateOpen(false)}
              color="default"
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              onClick={() => {
                setConfirmDeactivateOpen(false);
                onDeactivate();
              }}
              variant="contained"
              style={{
                backgroundColor:
                  toActivate.length > 0 && toDeactivate.length === 0
                    ? '#2e7d32'
                    : toDeactivate.length > 0 && toActivate.length === 0
                      ? '#d32f2f'
                      : '#17497F',
                color: '#ffffff',
              }}
              disabled={loading}
            >
              Confirmar Alteração
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default UIBatchActionBar;
